import createReducer from "./createReducer";
import { addFiltering } from "./common/Filtering";
import { addColumnFilters } from "./common/ColumnFilters";

import { ASSET_ACTIONS } from "./actions/Assets";
import { SUPPRESS_SUCCESS_GROWL } from "utils/constants";
import { buildNoFileMessage, escapeUnderscoreForAPI, mergeItem, mergeItems, noop } from "utils/common";
import { downloadBlob } from "./Download";
import { isAfterEffectsAsset } from "widgets/grid/cells/AssetActions";

const initialAssetsState = {
	success: true,
	items: [],

	totalPages: 1,
	totalItems: 0,

	hasErrorLoading: false,
	isDeletingAsset:false,
	isLoaded: false,
	linkedCreatives:{
		linkedCreativesLoaded: false,
	},
	transcode: {
		isTranscoding: false,
	},
	dynamicVariables: {
		dynamicVariablesLoaded: false,
		variables: [],
		assetName: "",
		hasErrorLoading: false,
	},
};

const actionCreators = {
	fetchTotalAssets,

	clearAssets,
	loadAssets,
	fetchAssetDetail,
	deleteVersions,

	setAssetTypeFilter,

	uploadAssets,
	loadAssetDistincts,
	fetchLinkedCreatives,
	clearLinkedCreatives,

	transcodeVideo,
	addSocketAsset,
	addSocketAssets,
};

const handlers = {
	[ASSET_ACTIONS.RESET_ASSETS_LIST]: clearList,
	[ASSET_ACTIONS.LOAD_ASSETS_AJAX]: isLoadingAssets,
	[ASSET_ACTIONS.LOAD_ASSETS_SUCCESS]: initAssetsList,
	[ASSET_ACTIONS.LOAD_ASSETS_ERROR]: errorAssetsList,

	[ASSET_ACTIONS.CHANGE_ASSET_TYPE_FILTER]: updateFilters,

	[ASSET_ACTIONS.FETCH_ASSET_LINKED_CREATIVES_AJAX]:loadingLinkedCreatives,
	[ASSET_ACTIONS.FETCH_ASSET_LINKED_CREATIVES_SUCCESS]:receivedLinkedCreatives,
	[ASSET_ACTIONS.FETCH_ASSET_LINKED_CREATIVES_ERROR]:receiveErrorLinkedCreatives,
	[ASSET_ACTIONS.CLEAR_ASSET_LINKED_CREATIVES]:clearedLinkedCreatives,

	[ASSET_ACTIONS.FETCH_ASSET_DYNAMIC_VARIABLES_AJAX]: loadingDynamicVariables,
	[ASSET_ACTIONS.FETCH_ASSET_DYNAMIC_VARIABLES_SUCCESS]: initDynamicVariables,
	[ASSET_ACTIONS.FETCH_ASSET_DYNAMIC_VARIABLES_ERROR]: errorDynamicVariables,
	[ASSET_ACTIONS.CLEAR_ASSET_DYNAMIC_VARIABLES]: resetDynamicVariables,

	[ASSET_ACTIONS.DELETE_ASSET_AJAX]:initDeletingAsset,
	[ASSET_ACTIONS.DELETE_ASSET_SUCCESS]:removeAssets,

	[ASSET_ACTIONS.ADD_SOCKET_ASSET_ITEM]:addSocketItemToList,
	[ASSET_ACTIONS.ADD_SOCKET_ASSET_ITEMS]:addSocketItemsToList,


	[ASSET_ACTIONS.TRANSCODE_VIDEO_AJAX]: initTranscode,
	[ASSET_ACTIONS.TRANSCODE_VIDEO_SUCCESS]: successTranscode,
	[ASSET_ACTIONS.TRANSCODE_VIDEO_ERROR]: errorTranscode,
};


let reducerConfig = {
	typeRoot: "ASSETS",
	initialState: initialAssetsState,
	actionTypes: ASSET_ACTIONS,
	actionCreators,
	handlers,
};

reducerConfig = addFiltering(reducerConfig, { sort: "lastModified:DESC" });
reducerConfig = addColumnFilters(reducerConfig, {
	id: {
		name: "id",
		type: "Exclude",
		displayName: "ID",
	},
	encoded: {
		name: "encoded",
		type: "Checkbox",
		displayName: "Video Type",
		dictionaryName: "encoded",
	},
	filesize: {
		name: "filesize",
		type: "Range",
		displayName: "File Size",
	},
	lastModified: {
		name: "lastModified",
		type: "Date",
		displayName: "Last Modified",
	},
	assetType: {
		name: "assetType",
		type: "Checkbox",
		displayName: "Type",
	},
	dimensions: {
		name: "dimensions",
		type: "Checkbox",
		displayName: "Dimensions",
	},
});

const { setAdvancedFilter, updateColumnFilter } = reducerConfig.actionCreators;

export function fetchTotalAssets(libraryId, type){
	/*
		The UI has to distinguish between a library with no assets (optionally of a given `type`),
		and a fetch where filters have been applied such that no assets match .

		We can use this action to get a minimal payload from the server, and we just extract
		the `totalItems` field.

		e.g.
		dispatch(Assets.fetchTotalAssets(someLibrary)).then((total) => {
			if(total === 0){
				// Show no assets banner
			}
			else{
				// Show list of assets.
			}
		})

		This sidesteps any complications like column filters already being set, or search terms
		already being applied from prior selections.
	*/
	let params = {
		rpp: 1,
	};
	if(type){
		params.advancedFilter = `all(in(assetType,${type}))`;
	}

	return (dispatch) => {
		return dispatch({
			type: ASSET_ACTIONS.FETCH_TOTAL_ASSETS,
			meta:SUPPRESS_SUCCESS_GROWL,
			AJAX: {
				url: `ui/creative-libraries/${libraryId}/assets`,
				params,
				success: ASSET_ACTIONS.FETCH_TOTAL_ASSETS_SUCCESS,
				error: ASSET_ACTIONS.FETCH_TOTAL_ASSETS_ERROR,
			},
		}).then((response) => {
			return response.payload.data.totalItems;
		});
	};
}

export function clearAssets(){
	return (dispatch) => {
		dispatch({
			type: ASSET_ACTIONS.RESET_ASSETS_LIST,
		});

		return Promise.resolve({ success: true });
	};
}

function assetTypeAdvancedFilter(type){
	return `eq(assetType, ${type})`;
}

/**
 * Helper function to collate both content and variable tags into one "tags" query param.
 * Removes the individual params as well.
 */
function constructParams(filterInfo) {
	const params = { ...filterInfo };

	if (filterInfo.searchTerm) {
		params.searchTerm = escapeUnderscoreForAPI(filterInfo.searchTerm);
	}

	// Keep these references just in case something has those two set somewhere
	// We delete them and don't send them to the API
	delete params.contentTags;
	delete params.variableTags;

	return params;
}

export function loadAssets(libraryID, cancelToken){
	return (dispatch, getState) => {
		let state = getState();
		let { filterInfo } = state.assets;

		const params = constructParams(filterInfo);

		return dispatch({
			type: ASSET_ACTIONS.LOAD_ASSETS_AJAX,
			meta:SUPPRESS_SUCCESS_GROWL,
			AJAX: {
				url: `ui/creative-libraries/${libraryID}/assets`,
				params,
				success: ASSET_ACTIONS.LOAD_ASSETS_SUCCESS,
				error: ASSET_ACTIONS.LOAD_ASSETS_ERROR,
				cancel: ASSET_ACTIONS.LOAD_ASSETS_CANCEL,
				cancelToken,
			},
		});
	};
}

export function fetchAssetDetail(libraryId, assetType, assetId){
	/*
		The combined asset list we get from `/creative-libraries/{libraryId}/assets`
		truncates some type-specific values that we may need, so this fetches single
		asset details from the API.
	*/
	let assetTypeLower = assetType.toLowerCase();
	const urlByType = {
		image: `creative-libraries/${libraryId}/asset/image/${assetId}`,
		richload: `creative-libraries/${libraryId}/asset/richload/${assetId}`,
		video: `creative-libraries/${libraryId}/asset/video/${assetId}`,
	};

	let url = urlByType[assetTypeLower];

	if(!url){
		console.error("Attempted to fetch asset detail for invalid type: ", assetType);
		return;
	}

	return (dispatch, getState) => {
		return dispatch({
			type: ASSET_ACTIONS.FETCH_ASSET_DETAIL,
			meta: SUPPRESS_SUCCESS_GROWL,
			AJAX: {
				url,
				success: ASSET_ACTIONS.FETCH_ASSET_DETAIL_SUCCESS,
				error: ASSET_ACTIONS.FETCH_ASSET_DETAIL_ERROR,
			},
		});
	};
}

export function fetchNameConflicts(libraryId, assetType, filenames){
	return (dispatch) => {
		const validTypes = ["images", "videos", "richloads", "audios", "after-effects"];
		if(!validTypes.includes(assetType)){
			console.error("Attempted to fetch name conflicts for invalid type: ", assetType);
			return;
		}

		let files = filenames.map(name => ({ name }));
		let dataName = assetType;
		let prefix = "";

		if(assetType === "after-effects"){
			dataName = "afterEffectsPackages";
			prefix = "ui/";
		}

		return dispatch({
			type: ASSET_ACTIONS.FETCH_ASSET_NAME_CONFLICTS_AJAX,
			meta: SUPPRESS_SUCCESS_GROWL,
			AJAX: {
				url: `${prefix}creative-libraries/${libraryId}/${assetType}/conflicts`,
				method: "post",
				data: {
					[dataName]: files,
				},
				success: ASSET_ACTIONS.FETCH_ASSET_NAME_CONFLICTS_SUCCESS,
				error: ASSET_ACTIONS.FETCH_ASSET_NAME_CONFLICTS_ERROR,
			},
		}).then((response) => {
			if(response?.success){
				let conflicts = response.payload?.data?.[dataName];
				return conflicts;
			}
			throw { description: "There was a problem verifying name conflicts.  Please try again." };
		});
	};
}

export function transcodeVideo(libraryId, encodeRequest){
	/*
		We're only doing one for now, but we're using the encode-many endpoint.

		`encodeRequest` should look like:
		{
			"advvast": true,
			"items": [
				{
					"advvast": true,
					"customEncodes": [
						{
							"audio_bitrate": "string",
							"audio_gain": 0,
							"audio_sample_rate": "string",
							"frame_rate": 0,
							"height": 0,
							"video_bitrate": 0,
							"width": 0
						}
					],
					"height": 0,
					"name": "string",
					"overwrite": true,
					"type": "string",
					"videoSource": "string",
					"width": 0
				}
			],
			"overwrite": true,
			"type": "string"
		}
	*/
	return (dispatch) => {
		return dispatch({
			type: ASSET_ACTIONS.TRANSCODE_VIDEO_AJAX,
			AJAX: {
				url: `creative-libraries/${libraryId}/asset/video/encode-many`,
				method: "post",
				data: encodeRequest,
				success: ASSET_ACTIONS.TRANSCODE_VIDEO_SUCCESS,
				error: ASSET_ACTIONS.TRANSCODE_VIDEO_ERROR,
			},
		});
	};
}

export function setAssetTypeFilter(types){
	let advancedFilter = types.enabled ? assetTypeAdvancedFilter(types.level) : "";

	return (dispatch) => {
		dispatch({
			type: ASSET_ACTIONS.CHANGE_ASSET_TYPE_FILTER,
			payload: types,
		});

		dispatch(setAdvancedFilter(advancedFilter));
	};
}

export function deleteVersions(versions){
	return (dispatch) => {
		return dispatch({
			type:ASSET_ACTIONS.DELETE_VERSIONS_AJAX,
			AJAX:{
				// TODO: find out what URL this actually is
				url:"service/instantads",
				version:0,
				data:versions,
				method:"delete",
				success:ASSET_ACTIONS.DELETE_VERSIONS_SUCCESS,
				error:ASSET_ACTIONS.DELETE_VERSIONS_ERROR,
			},
		});
	};
}

export function uploadAssets(assetType, libraryId, formData, extraArgs){
	/*
		`formData` should be a FormData object, with a separate entry for each file,
		all with the same key, `files`.

		It may also include a `skip` or `overwrite` boolean value, to resolve name conflicts
		with existing assets in the library.

		Like:
			FormData: [
				{ files: File },
				{ files: File },
				{ skip: true },
			]
	*/
	const assetTypeLower = assetType.toLowerCase();
	const uploadURLRoot = `ui/creative-libraries/${libraryId}/`;
	const urlEndsByType = {
		video: "asset/video/upload-many",
		audio: "asset/audio/upload",
		image: "asset/image/upload-many",
		richload: "asset/richload/upload-many",
		text: "asset/text/import",
		//After Effects Packages do not have the asset/ in their URL slug because it's a single item upload
		"after-effect": "after-effects/upload",
	};

	let validTypes = Object.keys(urlEndsByType);

	if(!validTypes.includes(assetTypeLower)){
		console.error("Attempted to upload an invalid asset type: ", assetType);
		return;
	}

	const {
		onUploadProgress = noop,
		meta = {},
	} = extraArgs || {};

	let urlEnd = urlEndsByType[assetTypeLower];
	let url = `${uploadURLRoot}${urlEnd}`;

	return (dispatch) => {
		return dispatch({
			type: ASSET_ACTIONS.UPLOAD_ASSETS_AJAX,
			meta,
			AJAX: {
				url,
				method: "post",
				headers: { "Content-Type": "multipart/form-data" },
				data: formData,
				success: ASSET_ACTIONS.UPLOAD_ASSETS_SUCCESS,
				error: ASSET_ACTIONS.UPLOAD_ASSETS_ERROR,
				onUploadProgress,
			},
		});
	};
}

export function downloadAssets(libraryId, assetsIds, assetType, fileName) {
	return (dispatch) => {
		let url = `creative-libraries/${libraryId}/${assetType}s/download-many`;

		if(isAfterEffectsAsset(assetType)){
			url = `ui/creative-libraries/${libraryId}/after-effects/download-many`;
		}

		return dispatch({
			type: ASSET_ACTIONS.DOWNLOAD_ASSETS_AJAX,
			AJAX:{
				method:"post",
				url,
				responseType:"blob",
				data:{
					assetsIds,
					name: fileName,
				},
				success: ASSET_ACTIONS.DOWNLOAD_ASSETS_SUCCESS,
				error: ASSET_ACTIONS.DOWNLOAD_ASSETS_ERROR,
			},
			meta:{
				buildMessage:buildNoFileMessage,
				tracking:{
					eventCategory:"Action Menu",
					eventAction:"Download",
					eventLabel:"Asset",
					value:assetsIds.length,
				},
			},
		})?.then(resp => downloadBlob(dispatch, resp, fileName));
	};
}

export function loadAssetDistincts(libraryId, columnNameList){
	/*
		Fetches distinct values for the provided creativelibraries column names,
		and loads them as options for the corresponding columnFilters,
		i.e. for Checkbox filters.
	*/
	let commaSeparatedColumnNames = columnNameList.join(",");

	return (dispatch, getState) => {
		return dispatch({
			type: ASSET_ACTIONS.LOAD_CREATIVES_DISTINCTS_AJAX,
			meta:SUPPRESS_SUCCESS_GROWL,
			AJAX: {
				url: `ui/creative-libraries/${libraryId}/assets/distincts`,
				params: { distincts: commaSeparatedColumnNames },
				success: ASSET_ACTIONS.LOAD_CREATIVES_DISTINCTS_SUCCESS,
				error: ASSET_ACTIONS.LOAD_CREATIVES_DISTINCTS_ERROR,
			},
		}).then((response) => {
			if(response.success){
				let { data } = response.payload;
				let keys = Object.keys(data);

				keys.forEach((key) => {
					let distincts = data[key];
					/*
						The server response usually contains a null value, and an empty string,
						which we're not interested in.

						We can trim them out here, rather than have to add logic to ignore them
						every time we want to work with these values.
					*/
					let filteredDistincts = distincts.filter(item => (item !== null) && (item !== ""));

					// Patch the options into the corresponding columnFilters.
					dispatch(updateColumnFilter(key, {
						options: filteredDistincts,
					}));
				});
			}
		});
	};
}

function clearList(state, action){
	return {
		...initialAssetsState,
		assetTypeFilter: state.assetTypeFilter,
		filterInfo: state.filterInfo,
		columnFilters: state.columnFilters,
	};
}

function isLoadingAssets(state, action){
	return {
		...state,
		isLoaded: false,
	};
}

function initAssetsList(state, action){
	let { data } = action.payload;
	return {
		...state,
		...data,
		isLoaded: true,
		hasErrorLoading: false,
	};
}

function updateFilters(state, action){
	return {
		...state,
		assetTypeFilter: action.payload,
	};
}

function errorAssetsList(state, action){
	return {
		...state,
		isLoaded: true,
		hasErrorLoading: true,
	};
}

export function addSocketAsset(library, socketAsset){
	return (dispatch, getState) => {
		let currentLibraryId = getState().libraryProperties.id;
		if(currentLibraryId !== library?.id){ return; }

		return dispatch({
			type:ASSET_ACTIONS.ADD_SOCKET_ASSET_ITEM,
			payload:socketAsset,
		});
	};
}

export function addSocketAssets(library, socketAssets){
	return (dispatch, getState) => {
		let currentLibraryId = getState().libraryProperties.id;
		if(currentLibraryId !== library?.id){ return; }

		return dispatch({
			type:ASSET_ACTIONS.ADD_SOCKET_ASSET_ITEMS,
			payload:socketAssets,
		});
	};
}

function addSocketItemsToList(state, action) {
	let newItems = action.payload;
	let items = mergeItems(state.items, newItems);
	const newTotalItems = state.totalItems + (items.length - state.items.length);
	let { totalPages } = state;

	// Check if adding items will increase the total pages
	const maxTotalPages = Math.ceil(newTotalItems / state.filterInfo?.rpp);
	if (maxTotalPages > state.totalPages) {
		totalPages = maxTotalPages;
	}

	return {
		...state,
		totalPages,
		totalItems: newTotalItems,
		items,
	};
}

function addSocketItemToList(state, action){
	let newItem = action.payload;
	let items = mergeItem(state.items, newItem);
	const newTotalItems = state.totalItems + (items.length - state.items.length);
	let { totalPages } = state;

	// Check if adding items will increase the total pages
	const maxTotalPages = Math.ceil(newTotalItems / state.filterInfo?.rpp);
	if (maxTotalPages > state.totalPages) {
		totalPages = maxTotalPages;
	}

	return {
		...state,
		totalPages,
		totalItems: newTotalItems,
		items,
	};
}


export function initTranscode(state, action){
	return {
		...state,
		transcode: {
			isTranscoding: true,
		},
	};
}
export function successTranscode(state, action){
	return {
		...state,
		transcode: {
			isTranscoding: false,
		},
	};
}
export function errorTranscode(state, action){
	return {
		...state,
		transcode: {
			isTranscoding: false,
		},
	};
}

/**
 * Linked Creatives Methods
 ***************************/
export function clearLinkedCreatives() {
	return ((dispatch) => {
		dispatch({
			type: ASSET_ACTIONS.CLEAR_ASSET_LINKED_CREATIVES,
		});
	});
}

export function fetchLinkedCreatives(libraryId, dependentValues) {
	// assetId
	const { id, assetType, name } = dependentValues;
	const isAfterEffects = isAfterEffectsAsset(assetType);
	let url = `ui/creative-libraries/${libraryId}/asset/${assetType.toLowerCase()}/${id}/creatives`;

	if (isAfterEffects) {
		url = `ui/creative-libraries/${libraryId}/after-effects/${id}/creatives`;
	}

	return (dispatch) => {
		return dispatch({
			type: ASSET_ACTIONS.FETCH_ASSET_LINKED_CREATIVES_AJAX,
			AJAX: {
				url,
				success: ASSET_ACTIONS.FETCH_ASSET_LINKED_CREATIVES_SUCCESS,
				error: ASSET_ACTIONS.FETCH_ASSET_LINKED_CREATIVES_ERROR,
			},
			meta: SUPPRESS_SUCCESS_GROWL,
			payload: { name, isAfterEffects },
		});
	};
}

function loadingLinkedCreatives(state, action) {
	return {
		...state,
		linkedCreatives: {
			linkedCreativesLoaded: false,
		},
	};
}

function clearedLinkedCreatives(state, action){
	return {
		...state,
		linkedCreatives:{
			linkedCreativesLoaded: false,
		},
	};
}

function receivedLinkedCreatives(state, action) {
	let { data, original = {} } = action.payload
		, { name: assetName, isAfterEffects } = original
		, newLinked = {}
		, creatives = isAfterEffects ? data?.creatives : data // Adjustment for different response data from After Effects assets
		;

	if(creatives.length){
		newLinked = {
			assetName,
			data: creatives,
		};
	}

	return {
		...state,
		linkedCreatives:{
			...state.linkedCreatives,
			linkedCreativesLoaded: true,
			...newLinked,
		},
	};
}

function receiveErrorLinkedCreatives(state, action) {
	return {
		...state,
		linkedCreatives:{
			hasErrorLoading: true,
			linkedCreativesLoaded: true,
		},
	};
}

/**
 * After Effects Dynamic Variables Methods
 ***************************/
export function clearDynamicVariables() {
	return (dispatch => dispatch({ type: ASSET_ACTIONS.CLEAR_ASSET_DYNAMIC_VARIABLES }));
}

export function fetchDynamicVariables(libraryId, assetId, name) {
	return (dispatch) => {
		return dispatch({
			type: ASSET_ACTIONS.FETCH_ASSET_DYNAMIC_VARIABLES_AJAX,
			AJAX: {
				url: `ui/creative-libraries/${libraryId}/after-effects/${assetId}/dynamic-variables`,
				success: ASSET_ACTIONS.FETCH_ASSET_DYNAMIC_VARIABLES_SUCCESS,
				error: ASSET_ACTIONS.FETCH_ASSET_DYNAMIC_VARIABLES_ERROR,
			},
			meta: SUPPRESS_SUCCESS_GROWL,
			payload: name,
		});
	};
}

function loadingDynamicVariables(state, action) {
	return {
		...state,
		dynamicVariables: {
			...state.dynamicVariables,
			dynamicVariablesLoaded: false,
		},
	};
}

function initDynamicVariables(state, action) {
	return {
		...state,
		dynamicVariables: {
			...state.dynamicVariables,
			assetName: action.payload.original,
			variables: action.payload.data?.variables || [],
			dynamicVariablesLoaded: true,
		},
	};
}

function errorDynamicVariables(state, action) {
	return {
		...state,
		dynamicVariables:{
			...state.dynamicVariables,
			hasErrorLoading: true,
			dynamicVariablesLoaded: true,
		},
	};
}

function resetDynamicVariables(state, action){
	return {
		...state,
		dynamicVariables: { ...initialAssetsState.dynamicVariables },
	};
}

/**
 * Delete Assets
 ****************/
export function deleteAssets(libraryId, assetType, ids){
	// Use different url for After Effects Asset Types
	const isAfterEffectsAssetType = isAfterEffectsAsset(assetType);
	let url = `ui/creative-libraries/${libraryId}/asset/${assetType.toLowerCase()}`;
	if (isAfterEffectsAssetType) {
		url = `ui/creative-libraries/${libraryId}/after-effects?ids=${ids}`;
	}

	return (dispatch) => {
		return dispatch({
			type:ASSET_ACTIONS.DELETE_ASSET_AJAX,
			AJAX:{
				method:"delete",
				url,
				data: isAfterEffectsAssetType ? undefined : ids, // After effects assets pass ids through query instead of body params
				success:ASSET_ACTIONS.DELETE_ASSET_SUCCESS,
				error:ASSET_ACTIONS.DELETE_ASSET_ERROR,
			},
			meta:{
				buildMessage:deleteNotify,
			},
		});
	};
}
const BAD_REQ = 400;
function deleteNotify({ data, status }){
	if(data.code >= BAD_REQ || status >= BAD_REQ){
		return {
			id:new Date().getTime(),
			type:"error",
			message:"Unable to Delete Asset",
		};
	}

	let type = "success"
		, message = `${data.deletedIds.length} assets successfully deleted.`
		, messageHeader = "Deleted Assets";

	if(data.failedIds.length){
		type = "error";
		message = `${data.failedIds.length} assets failed to delete.`;
		messageHeader = "Unable to Delete Assets";

		if(data.deletedIds.length){
			type = "info";
			messageHeader = "Some Assets deleted";
		}
	}

	return {
		id:new Date().getTime(),
		type,
		message,
		messageHeader,
	};
}

function initDeletingAsset(state, action){
	return {
		...state,
		isDeletingAsset:true,
	};
}

function removeAssets(state, action){
	let { payload } = action
		, newItems = []
		, { deletedIds } = payload.data
		, newTotal = state.totalItems - deletedIds.length
		;

	newItems = state.items.filter((v) => {
		return deletedIds.indexOf(v.id) === -1;
	});

	return {
		...state,
		items:newItems,
		totalItems:newTotal,
		isDeletingAsset:false,
	};
}

export default createReducer(reducerConfig.initialState, reducerConfig.handlers);
export const Assets = reducerConfig.actionCreators;
export const PRIVATE = {
	...reducerConfig.handlers,
	deleteNotify,
	constructParams,
};
